import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {makeStyles, Divider, Grid, InputLabel, Typography, FormHelperText} from "@material-ui/core";
import CustomInput from "../form/controls/input/CustomInput";
import clsx from "clsx";
import {CustomButton, Select} from "@hapinest/theme";
import {useI18N} from "../../../common/common";
import {EDITABLE_FIELD} from "../../../common/constant";
import FormControl from "@material-ui/core/FormControl";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import {cloneDeep} from "lodash";

const useStyles = makeStyles((theme) => ({
    formControlRoot: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        display: "block"
    },
    formControlClassName: {
        width: "100%",
    },
    radio: {
        "&$checked": {
            color: theme.palette.primary.main
        }
    },
    checked: {},
    selectPaddingTop: {
        paddingTop: theme.spacing(2.25),
        "&:focus": {
            background: "unset !important"
        }
    },
    m0: {
        margin: 0
    },
    mt2: {
        marginTop: theme.spacing(2)
    },
    mb1: {
        marginBottom: theme.spacing(1)
    },
    mb1AndHalf: {
        marginBottom: theme.spacing(1.5)
    },
    mb2: {
        marginBottom: theme.spacing(2)
    },
    mb3: {
        marginBottom: theme.spacing(3)
    }
}));

const PersonalDetailField = ({
    name,
    title,
    display_value,
    editable,
    remoteResponse,
    fields,
    onFieldSave,
    formOnEditMode,
    setFormOnEditMode,
    loading,
    actionButton
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const [editMode, setEditMode] = useState(false);
    const [localFields, setLocalFields] = useState();
    const [saveLoading, setSaveLoading] = useState(false);

    useEffect(() => {
        setLocalFields(cloneDeep(fields));
    }, [JSON.stringify(fields)]);

    useEffect(() => {
        if (remoteResponse && ("success" in remoteResponse)) {
            if (remoteResponse?.success) {
                setSaveLoading(false);
                toggleEditMode();
            } else {
                const newLocalFields = cloneDeep(localFields);
                Object.keys(remoteResponse.fieldErrors).forEach((item) => {
                    newLocalFields[item].error = true;
                    newLocalFields[item].errorText = remoteResponse.fieldErrors[item];
                });
                setLocalFields(newLocalFields);
                setSaveLoading(false);
            }
        }
    }, [JSON.stringify(remoteResponse)]);

    const onEditModeToggleClick = () => {
        if (editMode) {
            const newLocalFields = cloneDeep(localFields);
            Object.keys(newLocalFields).forEach((key) => {
                newLocalFields[key].value = fields[key].value;
                newLocalFields[key].error = fields[key].error;
            });
            setLocalFields(newLocalFields);
        }
        toggleEditMode();
    };

    const toggleEditMode = (value = !editMode) => {
        setEditMode(value);
        setFormOnEditMode(value);
    };

    const onChange = (fieldKey, value) => {
        const newLocalFields = {...localFields};
        if(newLocalFields[fieldKey].transformationRule) {
            newLocalFields[fieldKey].value = doTransformation(newLocalFields[fieldKey].transformationRule, value);
        } else {
            newLocalFields[fieldKey].value = value;
        }
        setLocalFields(newLocalFields);
        validateFields();
    };

    const validateFields = () => {
        const newLocalFields = cloneDeep(localFields);
        let fieldsWithErrorStatuses = false;
        Object.keys(newLocalFields).forEach((key) => {
            const fieldValue = newLocalFields[key];
            for (const rule of fieldValue.validationRules) {
                if (checkRule(rule, fieldValue.value)) {
                    fieldValue.error = true;
                    fieldValue.errorText = rule.message;
                    fieldsWithErrorStatuses = true;
                    break;
                } else {
                    fieldValue.error = false;
                }
            }
        });
        setLocalFields(newLocalFields);
        return fieldsWithErrorStatuses;
    };

    const checkRule = (rule, value) => {
        let ruleFailed = false;
        switch (rule.type) {
            case "required":
                if (value.length === 0) {
                    ruleFailed = true;
                }
                break;
            case "maxLength":
                if (value.length > rule.value) {
                    ruleFailed = true;
                }
                break;
            case "pattern":
                if (rule.value && rule.value.length) {
                    const regex = new RegExp(rule.value);
                    ruleFailed = (regex.test(value) === false);
                }
                break;
            default:
                break;
        }

        return ruleFailed;
    };

    const doTransformation = (rule, value) => {
        let res = value;
        switch (rule) {
            case "capitalize":
                if (value.length) {
                    res = value.replace(/(\b[a-z])/g, (x) => x.toUpperCase());
                }
                break;
            default:
                break;
        }

        return res;
    };

    const onSave = () => {
        const fieldsWithErrorStatuses = validateFields();
        if (!fieldsWithErrorStatuses) {
            onFieldSave(name, cloneDeep(localFields));
            setSaveLoading(true);
        }
    };

    return (
        <Grid
            container
            spacing={1}
        >
            <Grid
                item
                container
                justify={"space-between"}
                xs={12}
            >
                <Grid item>
                    <Typography
                        variant={"subtitle1"}
                        className={classes.mb1AndHalf}
                    >
                        {title}
                    </Typography>
                </Grid>

                {editable !== EDITABLE_FIELD.FALSE && (
                    <Grid item>
                        <CustomButton
                            size={"small"}
                            onClick={onEditModeToggleClick}
                            disabled={(formOnEditMode && !editMode) || (editable === EDITABLE_FIELD.DISABLED)}
                            disableRipple
                        >
                            {editMode ? i18n("common.label.cancel") : i18n("view.profile.account_settings.edit")}
                        </CustomButton>
                    </Grid>
                )}
                {!!actionButton && actionButton}
            </Grid>

            {editMode ? (
                <>
                    {Object.keys(localFields).map((key) => (
                        <Grid
                            item
                            key={key}
                            {...(localFields[key].columns)}
                            className={classes.mb1}
                        >
                            {localFields[key].type === "text" && (
                                <CustomInput
                                    name={name}
                                    value={localFields[key].value}
                                    label={localFields[key].label}
                                    disabled={!localFields[key].enabled}
                                    helperText={localFields[key].error ? localFields[key]?.errorText : localFields[key].helperText}
                                    error={localFields[key].error}
                                    onChange={(value) => onChange(key, value)}
                                    classNameInTextField
                                    className={classes.m0}
                                />
                            )}

                            {localFields[key].type === "select" && (
                                <FormControl
                                    className={classes.formControlClassName}
                                    variant="outlined"
                                    error={localFields[key].error}
                                >
                                    <InputLabel id={name}>
                                        {localFields[key].label}
                                    </InputLabel>
                                    <Select
                                        labelId={name}
                                        name={name}
                                        rootClassName={classes.selectPaddingTop}
                                        entries={fields[key].options}
                                        label={localFields[key].label}
                                        onChange={(event, value) => onChange(key, value)}
                                        value={localFields[key].value}
                                    />
                                    {localFields[key].error && <FormHelperText>{localFields[key]?.errorText}</FormHelperText>}
                                </FormControl>
                            )}

                            {localFields[key].type === "radio" && (
                                <FormControl>
                                    <RadioGroup
                                        onChange={(event, value) => onChange(key, value)}
                                        classes={{root: classes.formControlRoot}}
                                        value={localFields[key].value}
                                    >
                                        {Object.keys(localFields[key].options).map((key2) => (
                                            <FormControlLabel
                                                key={localFields[key].options[key2].value}
                                                value={localFields[key].options[key2].value}
                                                checked={localFields[key].options[key2].value === localFields[key].value}
                                                control={
                                                    <Radio
                                                        disableRipple
                                                        classes={{root: classes.radio, checked: classes.checked}}
                                                    />
                                                }
                                                label={i18n(localFields[key].options[key2].label)}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            )}
                        </Grid>
                    ))}

                    <Grid item>
                        <CustomButton
                            loading={saveLoading && loading}
                            variant={"contained"}
                            onClick={onSave}
                        >
                            {i18n("view.profile.account_settings.save")}
                        </CustomButton>
                    </Grid>
                </>
            ) : (
                <Grid
                    item
                    xs={12}
                >
                    <Typography variant={"body1"}>
                        {display_value}
                    </Typography>
                </Grid>
            )}

            <Grid
                item
                xs={12}
            >
                <Divider className={clsx(classes.mt2, classes.mb3)} />
            </Grid>
        </Grid>
    );
};

PersonalDetailField.defaultProps = {

};

PersonalDetailField.propTypes = {
    name: PropTypes.string,
    title: PropTypes.string,
    display_value: PropTypes.any,
    editable: PropTypes.number,
    remoteResponse: PropTypes.object,
    fields: PropTypes.object,
    onFieldSave: PropTypes.func,
    formOnEditMode: PropTypes.bool,
    setFormOnEditMode: PropTypes.func,
    loading: PropTypes.bool,
    actionButton: PropTypes.node
};

export default PersonalDetailField;
